import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import AchievementIndicator from './AchievementIndicator';
import { ACHIEVEMENTS_FONT, achievementsColors, CARD_FILL, RADIUS_CARD } from './achievementsTheme';
import { achievementText, AchievementView } from './achievementsViewModel';

/**
 * Figma "Achievement / Card" (4092:27730) - 106x126, three to a row in the modal's grid. The
 * file's note:
 *
 *   Default - unlocked, no corner badge. Selected - shown on the profile; green check corner
 *   badge. Locked - #241B19 plate, #4A3B38 stroke, text/muted label, desaturated artwork,
 *   padlock corner badge. For an unlocked-but-not-selected card, use Default, or override the
 *   corner badge to Indicator / Achievement State=Empty.
 *
 * The grid in "01 · Entry point" draws every card locked; the states below are what the master
 * defines, so an unlocked card reads the same as the file's Default and Selected variants.
 */
export const CARD_WIDTH = 106;
export const CARD_HEIGHT = 126;
const BADGE_SIZE = 62;
const BADGE_TOP = 18;
const LABEL_TOP = 90;
const LABEL_WIDTH = 98;
const INDICATOR_LEFT = 83;
const INDICATOR_TOP = 3;

type Props = {
	achievement: AchievementView;
	/** Whether this one is on the profile - the green check in the corner. */
	selected: boolean;
	/** The card itself - opens the details. */
	onPress: () => void;
	/** The plus in the corner - puts this one on the profile. */
	onAdd: () => void;
	scale: number;
};

export const AchievementCard = ({ achievement, selected, onPress, onAdd, scale }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const fillId = useSvgId('achievementCard');
	const { t } = useTranslation();

	const { locked } = achievement;
	const label = t(achievement.shownTier.nameKey);

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={label}
			onPress={onPress}
			style={[styles.card, locked ? styles.cardLocked : styles.cardUnlocked]}
		>
			{!locked && (
				<View pointerEvents='none' style={StyleSheet.absoluteFill}>
					<Svg width='100%' height='100%'>
						<Defs>
							<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
								{CARD_FILL.map(([offset, color]) => (
									<Stop key={offset} offset={offset} stopColor={color} />
								))}
							</LinearGradient>
						</Defs>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
					</Svg>
				</View>
			)}

			<View style={styles.badge}>{achievement.renderBadge(BADGE_SIZE * scale)}</View>

			<Text
				numberOfLines={2}
				style={[styles.label, locked ? styles.labelLocked : styles.labelUnlocked]}
			>
				{label}
			</Text>

			{/* Locked keeps the padlock; an unlocked card carries the green check while it is on the
			    profile, and the plus that puts it there otherwise.
			    The plus is its own control - pressing it adds the achievement, pressing the card
			    anywhere else opens its details. */}
			{!locked && !selected ? (
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={achievementText(t, 'T_SITE_ACHIEVEMENTS_ADD_A11Y', [label])}
					onPress={onAdd}
					hitSlop={8}
					style={styles.indicator}
				>
					<AchievementIndicator scale={scale} state='add' />
				</Pressable>
			) : locked || selected ? (
				<AchievementIndicator
					scale={scale}
					state={locked ? 'locked' : 'selected'}
					style={styles.indicator}
				/>
			) : null}
		</Pressable>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		card: {
			width: CARD_WIDTH * scale,
			height: CARD_HEIGHT * scale,
			borderRadius: RADIUS_CARD * scale,
			borderWidth: Math.max(1, 1 * scale),
			overflow: 'hidden',
		},
		cardUnlocked: {
			borderColor: achievementsColors.panelBorder,
		},
		cardLocked: {
			backgroundColor: achievementsColors.lockedPlate,
			borderColor: achievementsColors.lockedBorder,
		},
		badge: {
			position: 'absolute',
			left: ((CARD_WIDTH - BADGE_SIZE) / 2) * scale,
			top: BADGE_TOP * scale,
			width: BADGE_SIZE * scale,
			height: BADGE_SIZE * scale,
		},
		// Figma "DS/Label/M": Sofia Sans SemiBold 12, line-height 1.2, centred on the card.
		label: {
			position: 'absolute',
			left: ((CARD_WIDTH - LABEL_WIDTH) / 2) * scale,
			top: LABEL_TOP * scale,
			width: LABEL_WIDTH * scale,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.2 * scale,
			textAlign: 'center',
		},
		labelUnlocked: {
			color: achievementsColors.gold50,
		},
		labelLocked: {
			color: achievementsColors.muted,
		},
		indicator: {
			position: 'absolute',
			left: INDICATOR_LEFT * scale,
			top: INDICATOR_TOP * scale,
		},
	});

export default AchievementCard;
